import { REPORT_GAIN_TYPE_COLUMN_LABEL } from "../../../utils/reports/reportDisplayLabels";

export type FieldColumn = {
  key: string;
  value: string;
  locked?: boolean;
  minWidth?: number;
  align?: "left" | "center" | "right";
};

export const SIF_POINT_TO_POINT_REPORT = "Point to Point Return";
export const SIF_RETURNS_REPORT = "Returns Report";

export const SIF_TABLE_SEGMENT = "SIF";

export const SIF_REPORT_TYPE_OPTIONS = [
  { value: "Holding", label: "Unrealized" },
  { value: "Realized", label: "Realized" },
  { value: "All", label: "All" },
  { value: "Transaction", label: "Transaction" },
  { value: "Transaction report", label: "Transaction Report" },
  { value: SIF_POINT_TO_POINT_REPORT, label: "Point to Point Return" },
  { value: SIF_RETURNS_REPORT, label: SIF_RETURNS_REPORT },
  { value: "Sector report", label: "Sector Report" },
  { value: "Cap report", label: "Market Cap Report" },
] as const;

export const SIF_QUICK_REPORTS = [
  { id: "holding", label: "Unrealized", reportType: "Holding" },
  { id: "realized", label: "Realized", reportType: "Realized" },
  { id: "all", label: "All", reportType: "All" },
  { id: "p2p", label: "Point to Point Return", reportType: SIF_POINT_TO_POINT_REPORT },
  { id: "returns", label: "Returns Report", reportType: SIF_RETURNS_REPORT },
];

export const INVESTOR_COLUMN: FieldColumn = {
  key: "investorDisplay",
  value: "Investor Name",
  locked: true,
  minWidth: 200,
};

/** aumsampaan SIF Holding preset */
export const SIF_UNREALIZED_HOLDING_COLUMNS: FieldColumn[] = [
  { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 200 },
  { key: "isin", value: "ISIN", minWidth: 96 },
  { key: "units", value: "Units", minWidth: 100 },
  { key: "currentPrice", value: "Buy Price", minWidth: 112 },
  { key: "investedAmount", value: "Invested", minWidth: 128 },
  { key: "currentAmount", value: "Current Value", minWidth: 128 },
  { key: "gainLoss", value: "P&L", minWidth: 132 },
  { key: "gainLossPercentage", value: "P&L %", minWidth: 96 },
];

export const SIF_REALIZED_COLUMNS: FieldColumn[] = [
  { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 200 },
  { key: "isin", value: "ISIN", minWidth: 96 },
  { key: "type", value: REPORT_GAIN_TYPE_COLUMN_LABEL, locked: true, minWidth: 132 },
  { key: "transactionDate", value: "Date", minWidth: 112 },
  { key: "units", value: "Units Sold", minWidth: 100 },
  { key: "purchaseNAV", value: "Sell Price", minWidth: 112 },
  { key: "avgBuyPrice", value: "Avg Buy Price", minWidth: 112 },
  { key: "saleValue", value: "Sale Value", minWidth: 128 },
  { key: "realisedPnl", value: "Realized P&L", minWidth: 132 },
  { key: "realisedPNLPercentage", value: "Realized P&L%", minWidth: 120 },
];

export const SIF_TRANSACTION_COLUMNS: FieldColumn[] = [
  { key: "transType", value: "Type", locked: true, minWidth: 120 },
  { key: "transactionDate", value: "Date", minWidth: 112 },
  { key: "folioNumber", value: "Folio Number", minWidth: 96 },
  { key: "units", value: "Units", minWidth: 100 },
  { key: "purchasePrice", value: "NAV", minWidth: 112 },
  { key: "currentPrice", value: "Current NAV", minWidth: 112 },
  { key: "amount", value: "Amount", minWidth: 128 },
  { key: "currentAmount", value: "Current Value", minWidth: 128 },
  { key: "unrealisedPNLCombined", value: "Unrealised P&L (₹ / %)", minWidth: 160 },
  { key: "realisedPNLCombined", value: "Realised P&L (₹ / %)", minWidth: 160 },
  { key: "days", value: "Avg. Days", minWidth: 88 },
];

export const SIF_TRANSACTION_DETAILS_COLUMNS: FieldColumn[] = [
  { key: "transactiondate", value: "Transaction Date", locked: true, minWidth: 112 },
  { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 220 },
  { key: "category", value: "Category", minWidth: 96 },
  { key: "transactionType", value: "Transaction Type", minWidth: 120 },
  { key: "folioNo", value: "Folio No.", minWidth: 96 },
  { key: "units", value: "Units", minWidth: 100 },
  { key: "amount", value: "Invested Amount", minWidth: 128 },
  { key: "currentValue", value: "Current Value", minWidth: 128 },
  { key: "currentDate", value: "Current Date", minWidth: 112 },
  { key: "cagr", value: "CAGR", minWidth: 96 },
  { key: "days", value: "Avg. Days", minWidth: 88 },
];

export const SIF_P2P_COLUMNS: FieldColumn[] = [
  { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 168 },
  { key: "type", value: REPORT_GAIN_TYPE_COLUMN_LABEL, locked: true, minWidth: 132 },
  { key: "purchaseDate", value: "Purchase Date", minWidth: 112 },
  { key: "currentDate", value: "Current Date", minWidth: 112 },
  { key: "days", value: "Avg. Days", minWidth: 88 },
  { key: "units", value: "Units", minWidth: 112 },
  { key: "currentNAV", value: "Price", minWidth: 128 },
  { key: "investedAmount", value: "Invested", minWidth: 140 },
  { key: "currentAmount", value: "Current Value", minWidth: 148 },
  { key: "avgPrice", value: "Avg Price", minWidth: 112 },
  { key: "xirr", value: "XIRR", minWidth: 96 },
  { key: "cagr", value: "CAGR", minWidth: 96 },
  { key: "unrealisedPNL", value: "Unrealised P&L", minWidth: 132 },
  { key: "unrealisedPNLPercentage", value: "Unrealised P&L%", minWidth: 120 },
  { key: "realisedPnl", value: "Realised P&L", minWidth: 132 },
  { key: "realisedPNLPercentage", value: "Realised P&L%", minWidth: 120 },
  { key: "folioNo", value: "Folio No", minWidth: 128 },
];

export const SIF_RETURNS_COLUMNS: FieldColumn[] = [
  { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 200 },
  { key: "units", value: "Units", minWidth: 100 },
  { key: "amount", value: "Investment Amount (₹)", minWidth: 160 },
  { key: "currentNav", value: "Current NAV", minWidth: 112 },
  { key: "currentVal", value: "Current Value (₹)", minWidth: 160 },
  { key: "weeklyReturn", value: "Weekly Return (₹)", minWidth: 140 },
  { key: "weeklyReturnPercentage", value: "Weekly Return (%)", minWidth: 140 },
];

export const SIF_SECTOR_COLUMNS: FieldColumn[] = [
  { key: "sector", value: "Sector Name", locked: true, align: "center", minWidth: 160 },
  { key: "amount", value: "Invested Amount (₹)", align: "center", minWidth: 132 },
  { key: "percentageShare", value: "Share (%)", align: "center", minWidth: 100 },
];

export const SIF_CAP_COLUMNS: FieldColumn[] = [
  { key: "category", value: "Category", locked: true, align: "center", minWidth: 140 },
  { key: "value", value: "Invested Amount (₹)", align: "center", minWidth: 160 },
];

export const SIF_ALL_COMMON_COLUMNS: FieldColumn[] = [
  { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 200 },
  { key: "isin", value: "ISIN", minWidth: 96 },
  { key: "type", value: REPORT_GAIN_TYPE_COLUMN_LABEL, locked: true, minWidth: 132 },
  { key: "folioNumber", value: "Folio Number", minWidth: 96 },
  { key: "units", value: "Units", minWidth: 100 },
  { key: "costValue", value: "Invested Amount", minWidth: 128 },
  { key: "currentValue", value: "Current Value", minWidth: 128 },
  { key: "gainLoss", value: "P&L", minWidth: 132 },
  { key: "gainLossPercentage", value: "P&L %", minWidth: 96 },
];

export const getColumnsForSifReportType = (reportType: string): FieldColumn[] => {
  switch (reportType) {
    case "Holding":
      return SIF_UNREALIZED_HOLDING_COLUMNS;
    case "Realized":
      return SIF_REALIZED_COLUMNS;
    case "All":
      return SIF_ALL_COMMON_COLUMNS;
    case "Transaction":
      return SIF_TRANSACTION_COLUMNS;
    case "Transaction report":
      return SIF_TRANSACTION_DETAILS_COLUMNS;
    case SIF_POINT_TO_POINT_REPORT:
      return SIF_P2P_COLUMNS;
    case SIF_RETURNS_REPORT:
      return SIF_RETURNS_COLUMNS;
    case "Sector report":
      return SIF_SECTOR_COLUMNS;
    case "Cap report":
      return SIF_CAP_COLUMNS;
    default:
      return SIF_UNREALIZED_HOLDING_COLUMNS;
  }
};

export const reportTypeNeedsDateRange = (reportType: string): boolean =>
  ["Holding", "Transaction report", SIF_POINT_TO_POINT_REPORT].includes(reportType);
